import React, { useState, useEffect } from 'react';
import { api, useAuth } from '@/App';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { CheckCircle2, XCircle, Eye, Loader2, RefreshCw, Clock, ChevronDown, ChevronRight, Filter } from 'lucide-react';
import { toast } from 'sonner';
import { ShiftLogDetailDialog } from '@/components/ShiftLogDetailDialog';
import { computeToolStats, computeDeviation } from '@/utils/toolLifeCalculations';

function ApprovalsPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('production');

  const [runs, setRuns] = useState([]);
  const [runsLoading, setRunsLoading] = useState(true);
  const [selectedRun, setSelectedRun] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [action, setAction] = useState('APPROVE');
  const [comment, setComment] = useState('');
  const [processing, setProcessing] = useState(false);

  const [tiLogs, setTiLogs] = useState([]);
  const [tiLoading, setTiLoading] = useState(false);
  const [tiSelected, setTiSelected] = useState(null);
  const [tiDialogOpen, setTiDialogOpen] = useState(false);
  const [tiAction, setTiAction] = useState('approve');
  const [tiRejectReason, setTiRejectReason] = useState('');
  const [tiProcessing, setTiProcessing] = useState(false);
  const [tiDetailLog, setTiDetailLog] = useState(null);
  const [tiDetailOpen, setTiDetailOpen] = useState(false);
  const [tiCombinedPreviewOpen, setTiCombinedPreviewOpen] = useState(false);
  const [tiCombinedPreviewLogs, setTiCombinedPreviewLogs] = useState([]);
  const [tiCombinedPreviewDate, setTiCombinedPreviewDate] = useState('');
  const [groupActionType, setGroupActionType] = useState(null); // 'approve' | 'reject'
  const [groupActionScope, setGroupActionScope] = useState(null); // 'production' | 'tool-inspection'
  const [groupActionItems, setGroupActionItems] = useState([]);
  const [groupActionDate, setGroupActionDate] = useState('');
  const [groupActionDialogOpen, setGroupActionDialogOpen] = useState(false);
  const [groupActionProcessing, setGroupActionProcessing] = useState(false);
  const [groupActionComment, setGroupActionComment] = useState('');

  // Shared filters data
  const [machines, setMachines] = useState([]);

  // Production filters
  const [prDateFrom, setPrDateFrom] = useState('');
  const [prDateTo, setPrDateTo] = useState('');
  const [prShift, setPrShift] = useState('ALL');
  const [prMachine, setPrMachine] = useState('ALL');

  // Tool inspection filters
  const [tiDateFrom, setTiDateFrom] = useState('');
  const [tiDateTo, setTiDateTo] = useState('');
  const [tiShift, setTiShift] = useState('all');
  const [tiMachine, setTiMachine] = useState('all');

  // Grouping expansion
  const [prExpandedDates, setPrExpandedDates] = useState(new Set());
  const [tiExpandedDates, setTiExpandedDates] = useState(new Set());

  const canApproveTi = user?.role === 'MANAGER' || user?.role === 'OWNER';

  function groupByDate(items) {
    const byDate = {};
    items.forEach((item) => {
      if (!byDate[item.date]) byDate[item.date] = [];
      byDate[item.date].push(item);
    });
    return Object.entries(byDate).sort((a, b) => b[0].localeCompare(a[0]));
  }

  function fetchPendingRuns() {
    setRunsLoading(true);
    const params = { status: 'SUBMITTED' };
    if (prDateFrom) params.start_date = prDateFrom;
    if (prDateTo) params.end_date = prDateTo;
    if (prMachine && prMachine !== 'ALL') params.machine_id = prMachine;
    if (prShift && prShift !== 'ALL') params.shift = prShift;
    api
      .get('/production-runs', { params })
      .then(function (res) {
        setRuns(res.data);
      })
      .catch(function () {
        toast.error('Failed to load production runs');
      })
      .finally(function () {
        setRunsLoading(false);
      });
  }

  function fetchSubmittedShiftLogs() {
    setTiLoading(true);
    const params = { status: 'Submitted' };
    if (tiDateFrom) params.date_from = tiDateFrom;
    if (tiDateTo) params.date_to = tiDateTo;
    if (tiMachine && tiMachine !== 'all') params.machine_id = tiMachine;
    if (tiShift && tiShift !== 'all') params.shift = tiShift;
    api
      .get('/shift-logs', { params })
      .then(function (res) {
        setTiLogs(res.data || []);
      })
      .catch(function () {
        toast.error('Failed to load tool inspection logs');
      })
      .finally(function () {
        setTiLoading(false);
      });
  }

  useEffect(function () {
    api
      .get('/machines')
      .then(function (res) {
        setMachines(res.data || []);
      })
      .catch(function () {
        setMachines([]);
      });
    fetchPendingRuns();
    fetchSubmittedShiftLogs();
  }, []);

  function openActionDialog(run, actionType) {
    setSelectedRun(run);
    setAction(actionType);
    setComment('');
    setDialogOpen(true);
  }

  function openDetails(run) {
    if (!run?.id) return;
    navigate('/production-runs/' + run.id);
  }

  function handleApproval() {
    if (!selectedRun) return;
    setProcessing(true);
    api
      .post('/production-runs/' + selectedRun.id + '/approve', { action: action, comment: comment })
      .then(function () {
        toast.success(action === 'APPROVE' ? 'Approved' : 'Rejected');
        setDialogOpen(false);
        fetchPendingRuns();
        window.dispatchEvent(new CustomEvent('approvals-changed'));
      })
      .catch(function () {
        toast.error('Failed');
      })
      .finally(function () {
        setProcessing(false);
      });
  }

  function openTiDialog(log, type) {
    setTiSelected(log);
    setTiAction(type);
    setTiRejectReason('');
    setTiDialogOpen(true);
  }

  function openTiDetails(log) {
    if (!log?.id) return;
    api
      .get('/shift-logs/' + log.id)
      .then(function (res) {
        setTiDetailLog(res.data);
        setTiDetailOpen(true);
      })
      .catch(function () {
        toast.error('Failed to load inspection log details');
      });
  }

  function openTiCombinedPreview(date, dateLogs) {
    setTiCombinedPreviewDate(date);
    setTiCombinedPreviewLogs([]);
    setTiCombinedPreviewOpen(true);
    Promise.all(dateLogs.map(function (log) { return api.get('/shift-logs/' + log.id).then(function (r) { return r.data; }); }))
      .then(function (fullLogs) {
        var sorted = fullLogs.sort(function (a, b) {
          var shiftOrder = (a.shift || '').localeCompare(b.shift || '');
          if (shiftOrder !== 0) return shiftOrder;
          return (a.machine_code || '').localeCompare(b.machine_code || '');
        });
        setTiCombinedPreviewLogs(sorted);
      })
      .catch(function () {
        toast.error('Failed to load combined preview');
        setTiCombinedPreviewOpen(false);
      });
  }

  function handleTiAction() {
    if (!tiSelected) return;
    setTiProcessing(true);
    const id = tiSelected.id;
    const req =
      tiAction === 'approve'
        ? api.patch('/shift-logs/' + id + '/approve')
        : api.patch('/shift-logs/' + id + '/reject', { reason: tiRejectReason || '' });
    req
      .then(function () {
        toast.success(tiAction === 'approve' ? 'Shift log approved' : 'Shift log rejected');
        setTiDialogOpen(false);
        fetchSubmittedShiftLogs();
        window.dispatchEvent(new CustomEvent('approvals-changed'));
      })
      .catch(function (err) {
        toast.error(err.response?.data?.detail || 'Action failed');
      })
      .finally(function () {
        setTiProcessing(false);
      });
  }

  function openGroupAction(scope, actionType, items, date) {
    setGroupActionScope(scope);
    setGroupActionType(actionType);
    setGroupActionItems(items);
    setGroupActionDate(date);
    setGroupActionComment('');
    setGroupActionDialogOpen(true);
  }

  function handleGroupAction() {
    if (groupActionItems.length === 0 || !groupActionScope || !groupActionType) return;
    if (groupActionType === 'reject' && !groupActionComment.trim()) return;
    setGroupActionProcessing(true);
    const isApprove = groupActionType === 'approve';
    const promises = [];
    if (groupActionScope === 'production') {
      groupActionItems.forEach(function (run) {
        promises.push(
          api.post('/production-runs/' + run.id + '/approve', {
            action: isApprove ? 'APPROVE' : 'REJECT',
            comment: groupActionComment,
          })
        );
      });
    } else {
      groupActionItems.forEach(function (log) {
        promises.push(
          isApprove
            ? api.patch('/shift-logs/' + log.id + '/approve')
            : api.patch('/shift-logs/' + log.id + '/reject', { reason: groupActionComment })
        );
      });
    }
    Promise.all(promises)
      .then(function () {
        toast.success(
          isApprove
            ? `${groupActionItems.length} item(s) approved`
            : `${groupActionItems.length} item(s) rejected`
        );
        setGroupActionDialogOpen(false);
        fetchPendingRuns();
        fetchSubmittedShiftLogs();
        window.dispatchEvent(new CustomEvent('approvals-changed'));
      })
      .catch(function (err) {
        toast.error(err.response?.data?.detail || 'One or more actions failed');
      })
      .finally(function () {
        setGroupActionProcessing(false);
      });
  }

  let productionRows;
  if (runsLoading) {
    productionRows = (
      <TableRow>
        <TableCell colSpan={11} className="text-center py-8">
          <Loader2 className="w-6 h-6 animate-spin mx-auto" />
        </TableCell>
      </TableRow>
    );
  } else if (runs.length === 0) {
    productionRows = (
      <TableRow>
        <TableCell colSpan={11} className="text-center py-12">
          <CheckCircle2 className="w-12 h-12 mx-auto mb-3 text-green-300" />
          <p className="text-slate-500">All caught up!</p>
        </TableCell>
      </TableRow>
    );
  } else {
    productionRows = groupByDate(runs).map(function (entry) {
      var date = entry[0];
      var dateRuns = entry[1];
      const shifts = Array.from(
        new Set(
          dateRuns
            .map(function (r) {
              return r.shift;
            })
            .filter(Boolean),
        ),
      ).sort();
      const machinesForDate = Array.from(
        new Set(
          dateRuns
            .map(function (r) {
              return r.machine_code;
            })
            .filter(Boolean),
        ),
      ).sort();
      const expanded = prExpandedDates.has(date);
      return (
        <React.Fragment key={date}>
          <TableRow className="bg-slate-50/80 hover:bg-slate-100">
            <TableCell className="font-mono">
              <Button
                variant="ghost"
                size="icon"
                className="h-7 w-7 mr-2"
                onClick={function () {
                  setPrExpandedDates(function (prev) {
                    const next = new Set(prev);
                    if (next.has(date)) next.delete(date);
                    else next.add(date);
                    return next;
                  });
                }}
              >
                {expanded ? (
                  <ChevronDown className="w-4 h-4" />
                ) : (
                  <ChevronRight className="w-4 h-4" />
                )}
              </Button>
              {date}
            </TableCell>
            <TableCell colSpan={2} className="text-sm">
              Shifts: {shifts.join(', ') || '—'}
            </TableCell>
            <TableCell colSpan={3} className="text-sm font-mono">
              Machines: {machinesForDate.join(', ') || '—'}
            </TableCell>
            <TableCell colSpan={5} className="text-right">
              <div className="flex items-center justify-between">
                <span className="text-sm">{dateRuns.length} run(s)</span>
                <div className="flex items-center gap-1">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={function () {
                    openGroupAction('production', 'approve', dateRuns, date);
                  }}
                  className="h-8 text-green-600 hover:bg-green-50"
                  data-testid={'approve-all-' + date}
                >
                  <CheckCircle2 className="w-4 h-4" />
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={function () {
                    openGroupAction('production', 'reject', dateRuns, date);
                  }}
                  className="h-8 text-red-600 hover:bg-red-50"
                  data-testid={'reject-all-' + date}
                >
                  <XCircle className="w-4 h-4" />
                </Button>
                </div>
              </div>
            </TableCell>
          </TableRow>
          {expanded &&
            dateRuns.map(function (run) {
              return (
                <TableRow
                  key={run.id}
                  className="hover:bg-slate-50"
                  data-testid={'approval-row-' + run.id}
                >
                  <TableCell className="font-mono pl-10">{run.date}</TableCell>
                  <TableCell>
                    <Badge
                      variant="outline"
                      className={
                        run.shift === 'DAY'
                          ? 'bg-yellow-50 text-yellow-700'
                          : 'bg-indigo-50 text-indigo-700'
                      }
                    >
                      {run.shift}
                    </Badge>
                  </TableCell>
                  <TableCell className="font-medium">{run.machine_code}</TableCell>
                  <TableCell className="font-mono">
                    {Array.isArray(run.parts) && run.parts.length > 1
                      ? `Multiple (${run.parts.length})`
                      : (run.part_no || (run.parts && run.parts[0]?.part_no) || '')}
                  </TableCell>
                  <TableCell className="font-mono text-sm">
                    {run.derivative || '—'}
                  </TableCell>
                  <TableCell className="font-mono text-sm">
                    {run.setup_stage || '—'}
                  </TableCell>
                  <TableCell className="text-right font-mono text-sm">
                    {(run.cycle_time || 0) + 's'}
                  </TableCell>
                  <TableCell>{run.supervisor_name}</TableCell>
                  <TableCell className="text-right font-mono font-medium">
                    {run.metrics.produced_qty.toLocaleString()}
                  </TableCell>
                  <TableCell className="text-right font-mono">
                    <span
                      className={run.metrics.rejected_qty > 0 ? 'text-red-600' : undefined}
                    >
                      {run.metrics.rejected_qty}
                    </span>
                  </TableCell>
                  <TableCell>
                    <div className="flex items-center gap-1">
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={function () {
                          openDetails(run);
                        }}
                        className="h-9 w-9"
                        data-testid={'view-details-' + run.id}
                      >
                        <Eye className="w-4 h-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={function () {
                          openActionDialog(run, 'APPROVE');
                        }}
                        className="h-9 w-9 text-green-600 hover:bg-green-50"
                        data-testid={'approve-' + run.id}
                      >
                        <CheckCircle2 className="w-4 h-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={function () {
                          openActionDialog(run, 'REJECT');
                        }}
                        className="h-9 w-9 text-red-600 hover:bg-red-50"
                        data-testid={'reject-' + run.id}
                      >
                        <XCircle className="w-4 h-4" />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              );
            })}
        </React.Fragment>
      );
    });
  }

  let tiRows;
  if (tiLoading) {
    tiRows = (
      <TableRow>
        <TableCell colSpan={8} className="text-center py-8">
          <Loader2 className="w-6 h-6 animate-spin mx-auto" />
        </TableCell>
      </TableRow>
    );
  } else if (tiLogs.length === 0) {
    tiRows = (
      <TableRow>
        <TableCell colSpan={8} className="text-center py-12">
          <CheckCircle2 className="w-12 h-12 mx-auto mb-3 text-green-300" />
          <p className="text-slate-500">No submitted inspection logs</p>
        </TableCell>
      </TableRow>
    );
  } else {
    tiRows = groupByDate(tiLogs).map(function (entry) {
      var date = entry[0];
      var dateLogs = entry[1];
      const shifts = Array.from(
        new Set(
          dateLogs
            .map(function (l) {
              return l.shift;
            })
            .filter(Boolean),
        ),
      ).sort();
      const machinesForDate = Array.from(
        new Set(
          dateLogs
            .map(function (l) {
              return l.machine_code;
            })
            .filter(Boolean),
        ),
      ).sort();
      const expanded = tiExpandedDates.has(date);
      const submittedCount = dateLogs.filter(function (l) {
        return l.status === 'Submitted';
      }).length;
      return (
        <React.Fragment key={date}>
          <TableRow className="bg-slate-50/80 hover:bg-slate-100">
            <TableCell className="font-mono">
              <Button
                variant="ghost"
                size="icon"
                className="h-7 w-7 mr-2"
                onClick={function () {
                  setTiExpandedDates(function (prev) {
                    const next = new Set(prev);
                    if (next.has(date)) next.delete(date);
                    else next.add(date);
                    return next;
                  });
                }}
              >
                {expanded ? (
                  <ChevronDown className="w-4 h-4" />
                ) : (
                  <ChevronRight className="w-4 h-4" />
                )}
              </Button>
              {date}
            </TableCell>
            <TableCell className="text-sm">Shifts: {shifts.join(', ') || '—'}</TableCell>
            <TableCell className="text-sm font-mono">
              Machines: {machinesForDate.join(', ') || '—'}
            </TableCell>
            <TableCell colSpan={4} className="text-sm">
              {submittedCount} submitted, {dateLogs.length} log(s)
            </TableCell>
            <TableCell>
              <div className="flex items-center gap-1">
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={function () {
                    openTiCombinedPreview(date, dateLogs);
                  }}
                  className="h-9 w-9 text-slate-600 hover:bg-slate-100"
                  data-testid={'ti-combined-preview-' + date}
                >
                  <Eye className="w-4 h-4" />
                </Button>
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={function () {
                    openGroupAction('tool-inspection', 'approve', dateLogs, date);
                  }}
                  className="h-9 w-9 text-green-600 hover:bg-green-50"
                  data-testid={'ti-approve-all-' + date}
                >
                  <CheckCircle2 className="w-4 h-4" />
                </Button>
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={function () {
                    openGroupAction('tool-inspection', 'reject', dateLogs, date);
                  }}
                  className="h-9 w-9 text-red-600 hover:bg-red-50"
                  data-testid={'ti-reject-all-' + date}
                >
                  <XCircle className="w-4 h-4" />
                </Button>
              </div>
            </TableCell>
          </TableRow>
          {expanded &&
            dateLogs.map(function (log) {
              return (
                <TableRow
                  key={log.id}
                  className="hover:bg-slate-50"
                  data-testid={'ti-approval-row-' + log.id}
                >
                  <TableCell className="font-mono text-sm pl-10">{log.date}</TableCell>
                  <TableCell>{log.shift}</TableCell>
                  <TableCell className="font-mono font-medium">
                    {log.machine_code}
                  </TableCell>
                  <TableCell className="font-mono text-sm">{log.part_no || '-'}</TableCell>
                  <TableCell>
                    <Badge
                      variant="outline"
                      className={
                        log.operation === 'OBR'
                          ? 'bg-blue-50 text-blue-700'
                          : 'bg-amber-50 text-amber-700'
                      }
                    >
                      {log.operation}
                    </Badge>
                  </TableCell>
                  <TableCell>{log.shift_incharge || '-'}</TableCell>
                  <TableCell>
                    <Badge
                      variant="outline"
                      className="bg-blue-100 text-blue-700 border-blue-200"
                    >
                      {log.status}
                    </Badge>
                  </TableCell>
                  <TableCell>
                    <div className="flex items-center gap-1">
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={function () {
                          openTiDetails(log);
                        }}
                        className="h-9 w-9"
                        data-testid={'ti-view-' + log.id}
                      >
                        <Eye className="w-4 h-4" />
                      </Button>
                      {canApproveTi && log.status === 'Submitted' && (
                        <>
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={function () {
                              openTiDialog(log, 'approve');
                            }}
                            className="h-9 w-9 text-green-600 hover:bg-green-50"
                            data-testid={'ti-approve-' + log.id}
                          >
                            <CheckCircle2 className="w-4 h-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={function () {
                              openTiDialog(log, 'reject');
                            }}
                            className="h-9 w-9 text-red-600 hover:bg-red-50"
                            data-testid={'ti-reject-' + log.id}
                          >
                            <XCircle className="w-4 h-4" />
                          </Button>
                        </>
                      )}
                    </div>
                  </TableCell>
                </TableRow>
              );
            })}
        </React.Fragment>
      );
    });
  }

  return (
    <div className="space-y-6 animate-fade-in" data-testid="approvals-page">
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h1 className="font-heading text-3xl font-bold text-slate-900">Approvals</h1>
          <p className="text-slate-500">Review and approve submitted items across all modules.</p>
        </div>
        <Button
          variant="outline"
          onClick={() => {
            fetchPendingRuns();
            fetchSubmittedShiftLogs();
          }}
          data-testid="refresh-approvals-btn"
        >
          <RefreshCw className={'w-4 h-4 mr-2 ' + (runsLoading || tiLoading ? 'animate-spin' : '')} /> Refresh
        </Button>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="grid w-full max-w-xl grid-cols-3 bg-slate-100/80 p-1 rounded-lg shadow-sm">
          <TabsTrigger
            value="production"
            className="text-sm font-medium rounded-md data-[state=active]:bg-white data-[state=active]:text-slate-900 data-[state=active]:shadow"
          >
            Production Runs
          </TabsTrigger>
          <TabsTrigger
            value="tool-inspection"
            className="text-sm font-medium rounded-md data-[state=active]:bg-white data-[state=active]:text-slate-900 data-[state=active]:shadow"
          >
            Tool Inspections
          </TabsTrigger>
          <TabsTrigger
            value="future"
            className="text-sm font-medium rounded-md data-[state=active]:bg-white data-[state=active]:text-slate-900 data-[state=active]:shadow"
          >
            Future
          </TabsTrigger>
        </TabsList>

        {/* Production Run Approvals */}
        <TabsContent value="production" className="mt-6 space-y-4">
          <Card className="border-slate-200 bg-blue-50">
            <CardContent className="py-4 flex items-center gap-4">
              <div className="p-3 rounded-lg bg-blue-100">
                <Clock className="w-6 h-6 text-blue-600" />
              </div>
              <div>
                <p className="font-mono text-2xl font-bold text-blue-900">{runs.length}</p>
                <p className="text-sm text-blue-700">Pending production runs</p>
              </div>
            </CardContent>
          </Card>

          <Card className="border-slate-200">
            <CardHeader className="pb-4">
              <CardTitle className="text-lg flex items-center gap-2">
                <Filter className="w-5 h-5" />
                Filters
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3 items-end">
                <div className="space-y-1.5">
                  <label className="text-xs font-medium text-slate-700">From</label>
                  <Input
                    type="date"
                    value={prDateFrom}
                    onChange={function (e) {
                      setPrDateFrom(e.target.value);
                    }}
                    className="h-10 w-40"
                  />
                </div>
                <div className="space-y-1.5">
                  <label className="text-xs font-medium text-slate-700">To</label>
                  <Input
                    type="date"
                    value={prDateTo}
                    onChange={function (e) {
                      setPrDateTo(e.target.value);
                    }}
                    className="h-10 w-40"
                  />
                </div>
                <div className="space-y-1.5">
                  <label className="text-xs font-medium text-slate-700">Machine</label>
                  <Select value={prMachine} onValueChange={setPrMachine}>
                    <SelectTrigger className="h-10 text-sm">
                      <SelectValue placeholder="All Machines" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="ALL">All Machines</SelectItem>
                      {machines.map(function (m) {
                        return (
                          <SelectItem key={m.id} value={m.id}>
                            {m.code}
                          </SelectItem>
                        );
                      })}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-1.5">
                  <label className="text-xs font-medium text-slate-700">Shift</label>
                  <Select value={prShift} onValueChange={setPrShift}>
                    <SelectTrigger className="h-10 text-sm">
                      <SelectValue placeholder="All Shifts" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="ALL">All Shifts</SelectItem>
                      <SelectItem value="DAY">Day</SelectItem>
                      <SelectItem value="NIGHT">Night</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-1.5">
                  <label className="text-xs font-medium text-slate-700">&nbsp;</label>
                  <div className="flex gap-2">
                    <Button
                      variant="outline"
                      onClick={function () {
                        setPrDateFrom('');
                        setPrDateTo('');
                        setPrShift('ALL');
                        setPrMachine('ALL');
                        setPrExpandedDates(new Set());
                        fetchPendingRuns();
                      }}
                      className="h-10 w-full"
                    >
                      Clear
                    </Button>
                    <Button
                      onClick={function () {
                        fetchPendingRuns();
                      }}
                      className="h-10 w-full"
                    >
                      Search
                    </Button>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="border-slate-200">
            <CardHeader>
              <CardTitle>Submitted Runs</CardTitle>
              <CardDescription>Approve or reject submitted production runs.</CardDescription>
            </CardHeader>
            <CardContent className="p-0">
              <Table className="data-table">
                <TableHeader>
                  <TableRow className="bg-slate-50">
                    <TableHead>Date</TableHead>
                    <TableHead>Shift</TableHead>
                    <TableHead>Machine</TableHead>
                    <TableHead>Part</TableHead>
                    <TableHead>Derivative</TableHead>
                    <TableHead>Setup</TableHead>
                    <TableHead className="text-right">Cycle Time</TableHead>
                    <TableHead>Supervisor</TableHead>
                    <TableHead className="text-right">Produced</TableHead>
                    <TableHead className="text-right">Rejected</TableHead>
                    <TableHead className="w-40">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>{productionRows}</TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Tool Inspection Approvals */}
        <TabsContent value="tool-inspection" className="mt-6 space-y-4">
          <Card className="border-slate-200 bg-emerald-50">
            <CardContent className="py-4 flex items-center gap-4">
              <div className="p-3 rounded-lg bg-emerald-100">
                <Clock className="w-6 h-6 text-emerald-600" />
              </div>
              <div>
                <p className="font-mono text-2xl font-bold text-emerald-900">{tiLogs.length}</p>
                <p className="text-sm text-emerald-700">Pending inspection logs</p>
              </div>
            </CardContent>
          </Card>

          <Card className="border-slate-200">
            <CardHeader className="pb-4">
              <CardTitle className="text-lg flex items-center gap-2">
                <Filter className="w-5 h-5" />
                Filters
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3 items-end">
                <div className="space-y-1.5">
                  <label className="text-xs font-medium text-slate-700">From</label>
                  <Input
                    type="date"
                    value={tiDateFrom}
                    onChange={function (e) {
                      setTiDateFrom(e.target.value);
                    }}
                    className="h-10 w-40"
                  />
                </div>
                <div className="space-y-1.5">
                  <label className="text-xs font-medium text-slate-700">To</label>
                  <Input
                    type="date"
                    value={tiDateTo}
                    onChange={function (e) {
                      setTiDateTo(e.target.value);
                    }}
                    className="h-10 w-40"
                  />
                </div>
                <div className="space-y-1.5">
                  <label className="text-xs font-medium text-slate-700">Machine</label>
                  <Select value={tiMachine} onValueChange={setTiMachine}>
                    <SelectTrigger className="h-10 text-sm">
                      <SelectValue placeholder="All Machines" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Machines</SelectItem>
                      {machines.map(function (m) {
                        return (
                          <SelectItem key={m.id} value={m.id}>
                            {m.code}
                          </SelectItem>
                        );
                      })}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-1.5">
                  <label className="text-xs font-medium text-slate-700">Shift</label>
                  <Select value={tiShift} onValueChange={setTiShift}>
                    <SelectTrigger className="h-10 text-sm">
                      <SelectValue placeholder="All Shifts" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Shifts</SelectItem>
                      <SelectItem value="Day">Day</SelectItem>
                      <SelectItem value="Night">Night</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-1.5">
                  <label className="text-xs font-medium text-slate-700">&nbsp;</label>
                  <div className="flex gap-2">
                    <Button
                      variant="outline"
                      onClick={function () {
                        setTiDateFrom('');
                        setTiDateTo('');
                        setTiShift('all');
                        setTiMachine('all');
                        setTiExpandedDates(new Set());
                        fetchSubmittedShiftLogs();
                      }}
                      className="h-10 w-full"
                    >
                      Clear
                    </Button>
                    <Button
                      onClick={function () {
                        fetchSubmittedShiftLogs();
                      }}
                      className="h-10 w-full"
                    >
                      Search
                    </Button>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="border-slate-200">
            <CardHeader>
              <CardTitle>Submitted Tool Inspection Logs</CardTitle>
              <CardDescription>Approve or reject submitted shift logs.</CardDescription>
            </CardHeader>
            <CardContent className="p-0">
              <Table className="data-table">
                <TableHeader>
                  <TableRow className="bg-slate-50">
                    <TableHead>Date</TableHead>
                    <TableHead>Shift</TableHead>
                    <TableHead>Machine</TableHead>
                    <TableHead>Part No</TableHead>
                    <TableHead>Operation</TableHead>
                    <TableHead>Incharge</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead className="w-40">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>{tiRows}</TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Future tab placeholder */}
        <TabsContent value="future" className="mt-6">
          <Card className="border-dashed border-slate-300">
            <CardHeader>
              <CardTitle>Future approvals</CardTitle>
              <CardDescription>
                This tab will house approvals for any additional verticals you add in the future.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <p className="text-sm text-slate-600">
                When new modules are introduced, their submitted items can be listed and approved here
                following the same pattern as production runs and tool inspections.
              </p>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Production run approve/reject dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="sm:max-w-md" data-testid="approval-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              {action === 'APPROVE' ? (
                <CheckCircle2 className="w-5 h-5 text-green-600" />
              ) : (
                <XCircle className="w-5 h-5 text-red-600" />
              )}
              {action === 'APPROVE' ? 'Approve Run' : 'Reject Run'}
            </DialogTitle>
            {selectedRun && (
              <DialogDescription>
                <span className="font-mono">
                  {selectedRun.date} | {selectedRun.machine_code} |{' '}
                  {Array.isArray(selectedRun.parts) && selectedRun.parts.length > 1
                    ? `Multiple (${selectedRun.parts.length})`
                    : (selectedRun.part_no || (selectedRun.parts && selectedRun.parts[0]?.part_no) || '')}
                </span>
              </DialogDescription>
            )}
          </DialogHeader>
          {action === 'REJECT' && (
            <div className="space-y-2 py-4">
              <label className="text-sm font-medium">Reason</label>
              <Textarea
                value={comment}
                onChange={function (e) {
                  setComment(e.target.value);
                }}
                placeholder="Enter reason..."
                className="min-h-24"
                data-testid="rejection-comment"
              />
            </div>
          )}
          {action === 'APPROVE' && (
            <div className="py-4">
              <p className="text-slate-600">Confirm approval?</p>
            </div>
          )}
          <DialogFooter>
            <Button
              variant="outline"
              onClick={function () {
                setDialogOpen(false);
              }}
              data-testid="cancel-action-btn"
            >
              Cancel
            </Button>
            <Button
              onClick={handleApproval}
              disabled={processing || (action === 'REJECT' && !comment.trim())}
              className={
                action === 'APPROVE'
                  ? 'bg-green-600 hover:bg-green-700'
                  : 'bg-red-600 hover:bg-red-700'
              }
              data-testid="confirm-action-btn"
            >
              {processing && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              {action}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Tool inspection approve/reject dialog */}
      <Dialog open={tiDialogOpen} onOpenChange={setTiDialogOpen}>
        <DialogContent className="sm:max-w-md" data-testid="ti-approval-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              {tiAction === 'approve' ? (
                <CheckCircle2 className="w-5 h-5 text-green-600" />
              ) : (
                <XCircle className="w-5 h-5 text-red-600" />
              )}
              {tiAction === 'approve' ? 'Approve Inspection Log' : 'Reject Inspection Log'}
            </DialogTitle>
            {tiSelected && (
              <DialogDescription>
                <span className="font-mono">
                  {tiSelected.date} | {tiSelected.shift} | {tiSelected.machine_code}
                </span>
              </DialogDescription>
            )}
          </DialogHeader>
          {tiAction === 'reject' && (
            <div className="space-y-2 py-4">
              <label className="text-sm font-medium">Rejection Reason (optional)</label>
              <Textarea
                value={tiRejectReason}
                onChange={function (e) {
                  setTiRejectReason(e.target.value);
                }}
                placeholder="Enter reason for rejection..."
                data-testid="ti-reject-reason-input"
              />
            </div>
          )}
          {tiAction === 'approve' && (
            <div className="py-4">
              <p className="text-slate-600">Confirm approval?</p>
            </div>
          )}
          <DialogFooter>
            <Button
              variant="outline"
              onClick={function () {
                setTiDialogOpen(false);
              }}
            >
              Cancel
            </Button>
            <Button
              onClick={handleTiAction}
              disabled={tiProcessing}
              className={
                tiAction === 'approve'
                  ? 'bg-green-600 hover:bg-green-700'
                  : 'bg-red-600 hover:bg-red-700'
              }
              data-testid="ti-confirm-action-btn"
            >
              {tiProcessing && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              {tiAction === 'approve' ? 'Approve' : 'Reject'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Group action (approve all / reject all) dialog */}
      <Dialog open={groupActionDialogOpen} onOpenChange={setGroupActionDialogOpen}>
        <DialogContent className="sm:max-w-md" data-testid="group-action-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              {groupActionType === 'approve' ? (
                <CheckCircle2 className="w-5 h-5 text-green-600" />
              ) : (
                <XCircle className="w-5 h-5 text-red-600" />
              )}
              {groupActionType === 'approve' ? 'Approve All' : 'Reject All'} — {groupActionDate}
            </DialogTitle>
            <DialogDescription>
              {groupActionScope === 'production'
                ? `Approve or reject ${groupActionItems.length} production run(s) for ${groupActionDate}`
                : `Approve or reject ${groupActionItems.length} inspection log(s) for ${groupActionDate}`}
            </DialogDescription>
          </DialogHeader>
          {groupActionType === 'reject' && (
            <div className="space-y-2 py-4">
              <label className="text-sm font-medium">Reason (required)</label>
              <Textarea
                value={groupActionComment}
                onChange={function (e) {
                  setGroupActionComment(e.target.value);
                }}
                placeholder="Enter reason for rejection..."
                className="min-h-24"
                data-testid="group-reject-reason"
              />
            </div>
          )}
          {groupActionType === 'approve' && (
            <div className="py-4">
              <p className="text-slate-600">Confirm approval of all {groupActionItems.length} item(s)?</p>
            </div>
          )}
          <DialogFooter>
            <Button
              variant="outline"
              onClick={function () {
                setGroupActionDialogOpen(false);
              }}
            >
              Cancel
            </Button>
            <Button
              onClick={handleGroupAction}
              disabled={
                groupActionProcessing ||
                (groupActionType === 'reject' && !groupActionComment.trim())
              }
              className={
                groupActionType === 'approve'
                  ? 'bg-green-600 hover:bg-green-700'
                  : 'bg-red-600 hover:bg-red-700'
              }
              data-testid="group-confirm-btn"
            >
              {groupActionProcessing && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              {groupActionType === 'approve' ? 'Approve All' : 'Reject All'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Tool inspection detail dialog */}
      <ShiftLogDetailDialog log={tiDetailLog} open={tiDetailOpen} onOpenChange={setTiDetailOpen} />

      {/* Tool inspection combined preview dialog */}
      <TiCombinedPreviewDialog
        date={tiCombinedPreviewDate}
        logs={tiCombinedPreviewLogs}
        open={tiCombinedPreviewOpen}
        onOpenChange={setTiCombinedPreviewOpen}
      />
    </div>
  );
}

function tiGetStandardRange(standards, partId, toolCode, insertType, operation) {
  if (!standards?.length || !partId || !toolCode || !insertType) return null;
  var std = standards.find(function (s) {
    return s.part_id === partId &&
      s.tool_code === toolCode.toUpperCase() &&
      s.insert_type_code === insertType.toUpperCase();
  });
  if (!std) return null;
  if (operation === 'OTR') {
    return { min: std.frequency_cnc_ind_setup_min, max: std.frequency_cnc_ind_setup_max };
  }
  return { min: std.frequency_1st_setup_min, max: std.frequency_1st_setup_max };
}

function TiDeviationBadge({ deviation }) {
  if (!deviation) return React.createElement('span', { className: 'text-slate-300' }, '-');
  if (deviation.status === 'ok') return React.createElement('span', { className: 'text-green-600 font-medium' }, 'OK');
  if (deviation.status === 'below') return React.createElement('span', { className: 'text-red-600 font-medium' }, deviation.value);
  return React.createElement('span', { className: 'text-amber-600 font-medium' }, '+' + deviation.value);
}

function TiCombinedPreviewDialog({ date, logs, open, onOpenChange }) {
  const [standards, setStandards] = useState([]);

  useEffect(function () {
    if (!open) { setStandards([]); return; }
    api.get('/tool-life-standards').then(function (res) { setStandards(res.data || []); }).catch(function () { setStandards([]); });
  }, [open]);

  var detailStatusColors = {
    Draft: 'bg-slate-100 text-slate-700',
    Submitted: 'bg-blue-100 text-blue-700',
    Approved: 'bg-green-100 text-green-700',
    Rejected: 'bg-red-100 text-red-700',
  };

  function cpCheckpointLife(checkpoints, ci) {
    if (ci < 0 || !checkpoints?.length || ci >= checkpoints.length - 1) return null;
    return Math.abs((checkpoints[ci + 1].qty ?? 0) - (checkpoints[ci].qty ?? 0));
  }

  function CombinedToolSection({ tool, standardRange }) {
    var stats = computeToolStats(tool.checkpoints || []);
    var avgDeviation = computeDeviation(stats.avgLife, standardRange);
    return (
      <div className="border border-slate-200 rounded-lg p-3 space-y-2 bg-slate-50/50">
        <div className="font-medium text-sm">
          Tool {tool.tool_code} {tool.insert_type && '\u2014 ' + tool.insert_type}
        </div>
        {(tool.checkpoints || []).length > 0 && (
          <Table>
            <TableHeader>
              <TableRow className="bg-slate-100/50">
                <TableHead className="text-xs">#</TableHead>
                <TableHead className="text-xs">QTY</TableHead>
                <TableHead className="text-xs">Phase</TableHead>
                <TableHead className="text-xs">Phase Life</TableHead>
                <TableHead className="text-xs">Std Range</TableHead>
                <TableHead className="text-xs">Deviation</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {(tool.checkpoints || []).map(function (cp, ci) {
                var life = cpCheckpointLife(tool.checkpoints, ci);
                var deviation = computeDeviation(life, standardRange);
                var phase = cp.insert_phase;
                var totalPhases = cp.total_phases ?? 6;
                var phaseDisplay = phase == null || phase === '' ? '\u2014' : phase === 1 ? 'N' : phase + '/' + totalPhases;
                return (
                  <TableRow key={ci}>
                    <TableCell className="text-xs py-1">{ci + 1}</TableCell>
                    <TableCell className="font-mono text-sm py-1">{cp.qty}</TableCell>
                    <TableCell className="text-sm py-1">{phaseDisplay}</TableCell>
                    <TableCell className="font-mono text-sm py-1">{life !== null ? life : '-'}</TableCell>
                    <TableCell className="py-1">
                      <span className="text-xs font-mono text-slate-500">
                        {standardRange ? standardRange.min + '\u2013' + standardRange.max : <span className="text-slate-300">N/A</span>}
                      </span>
                    </TableCell>
                    <TableCell className="py-1 text-xs font-mono">
                      <TiDeviationBadge deviation={deviation} />
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        )}
        <div className="flex gap-4 text-xs text-slate-600 pt-1 border-t border-slate-100">
          <span>Inserts Used: <strong>{stats.totalInserts}</strong></span>
          <span className="flex items-center gap-1.5">
            Avg Phase Life: <strong>{stats.avgLife !== null ? stats.avgLife.toFixed(1) : '-'}</strong> pcs
            {avgDeviation && <TiDeviationBadge deviation={avgDeviation} />}
          </span>
        </div>
        {standardRange && (
          <div className="text-xs text-slate-500">
            Standard: {standardRange.min}{'\u2013'}{standardRange.max} pcs
          </div>
        )}
      </div>
    );
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="ti-combined-preview-dialog">
        <DialogHeader>
          <DialogTitle>Combined preview</DialogTitle>
          <DialogDescription>{date} {'\u2013'} all shift logs for this date</DialogDescription>
        </DialogHeader>
        <div className="space-y-6">
          {logs.length === 0 ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="w-6 h-6 animate-spin text-slate-400" />
            </div>
          ) : (
            logs.map(function (log) {
              return (
                <div key={log.id} className="border border-slate-200 rounded-lg p-4 bg-white space-y-3">
                  <div className="font-medium text-slate-800 border-b border-slate-100 pb-2">
                    {log.machine_code} | {log.shift} Shift
                  </div>
                  <div className="grid grid-cols-2 gap-3 text-sm">
                    <div><span className="text-slate-500">Part No:</span> <strong>{log.part_no || '-'}</strong></div>
                    <div>
                      <span className="text-slate-500">Operation: </span>
                      <Badge variant="outline" className={log.operation === 'OBR' ? 'bg-blue-50 text-blue-700' : 'bg-amber-50 text-amber-700'}>{log.operation}</Badge>
                    </div>
                    <div>
                      <span className="text-slate-500">Status: </span>
                      <Badge variant="outline" className={detailStatusColors[log.status]}>{log.status}</Badge>
                    </div>
                    <div><span className="text-slate-500">Shift Incharge:</span> <strong>{log.shift_incharge || '-'}</strong></div>
                  </div>
                  {log.rejection_reason && (
                    <div className="bg-red-50 border border-red-200 rounded-lg p-2 text-sm">
                      <span className="font-medium text-red-700">Rejection Reason:</span> {log.rejection_reason}
                    </div>
                  )}
                  {(log.tools || []).map(function (tool, ti) {
                    return (
                      <CombinedToolSection
                        key={ti}
                        tool={tool}
                        standardRange={tiGetStandardRange(standards, log.part_no_id, tool.tool_code, tool.insert_type, log.operation)}
                      />
                    );
                  })}
                  {log.summary?.insert_counts?.length > 0 && (
                    <div className="text-sm pt-1 border-t border-slate-100">
                      <strong className="text-slate-700">Insert summary:</strong>
                      <div className="flex gap-3 mt-1 flex-wrap">
                        {log.summary.insert_counts.map(function (ic, i) {
                          var used = ic.quantity_used ?? ic.quantity_issued ?? 0;
                          var issued = ic.quantity_issued ?? 0;
                          var hasIssued = typeof ic.quantity_used === 'number';
                          return (
                            <span key={i} className="font-mono">
                              {ic.insert_type_code}: {hasIssued ? 'issued ' + issued + ', used ' + used + ', remaining ' + Math.max(0, issued - used) : 'used ' + used}
                            </span>
                          );
                        })}
                      </div>
                    </div>
                  )}
                </div>
              );
            })
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default ApprovalsPage;
